import { unzipSync } from "fflate";

const ZIP_LOCAL_FILE_MAGIC = [0x50, 0x4b, 0x03, 0x04]; // "PK\x03\x04"

/**
 * Проверяет первые 4 байта файла на сигнатуру ZIP. Если расширение не помогло
 * (например, .ifc по факту является zip-ом), это резервный путь.
 */
async function isProbablyZip(file: File): Promise<boolean> {
  if (file.size < 4) {
    return false;
  }
  const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
  for (let i = 0; i < ZIP_LOCAL_FILE_MAGIC.length; i++) {
    if (head[i] !== ZIP_LOCAL_FILE_MAGIC[i]) {
      return false;
    }
  }
  return true;
}

function looksLikeIfczipName(name: string): boolean {
  return /\.ifczip$/i.test(name);
}

/**
 * Если файл — IFCZIP (или ZIP с IFC внутри), распаковывает первый .ifc
 * и возвращает новый `File` с теми же атрибутами. Иначе возвращает исходный.
 *
 * Распаковка идёт через `fflate` синхронно, in-memory — для проектных IFC
 * (десятки–сотни МБ) этого достаточно; стриминговую версию можно подключить
 * позже на Этапе 3, если потребуется.
 */
export async function maybeUnzipIfczip(file: File): Promise<File> {
  if (!file) {
    return file;
  }
  const isZipByName = looksLikeIfczipName(file.name);
  const isZipByMagic = !isZipByName && (await isProbablyZip(file));
  if (!isZipByName && !isZipByMagic) {
    return file;
  }

  const buffer = new Uint8Array(await file.arrayBuffer());
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(buffer);
  } catch (error) {
    if (isZipByName) {
      throw new Error(
        `Не удалось распаковать IFCZIP «${file.name}»: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }
    return file;
  }

  // Берём первый файл, у которого расширение .ifc (без учёта регистра).
  // Если такого нет — берём самую большую запись (часто это и есть IFC).
  let bestName: string | null = null;
  let bestBytes: Uint8Array | null = null;
  for (const [name, bytes] of Object.entries(entries)) {
    if (/\.ifc$/i.test(name) && (!bestBytes || bytes.length > bestBytes.length)) {
      bestName = name;
      bestBytes = bytes;
    }
  }
  if (!bestBytes) {
    for (const [name, bytes] of Object.entries(entries)) {
      if (!bestBytes || bytes.length > bestBytes.length) {
        bestName = name;
        bestBytes = bytes;
      }
    }
  }
  if (!bestBytes || !bestName) {
    if (isZipByName) {
      throw new Error(`IFCZIP «${file.name}» не содержит .ifc внутри.`);
    }
    return file;
  }

  // Имя IFC внутри архива берём от архива, но c расширением .ifc.
  const innerLeaf = bestName.split(/[\\/]/).pop() || "model.ifc";
  const outerWithoutExt = file.name.replace(/\.ifczip$/i, "");
  const outName = /\.ifc$/i.test(innerLeaf) ? `${outerWithoutExt || "model"}.ifc` : innerLeaf;

  // Копируем в свежий ArrayBuffer — иначе TS в ES2024-стиле ругается на
  // несовпадение SharedArrayBuffer/ArrayBuffer в параметризованном Uint8Array.
  const bytesCopy = new Uint8Array(bestBytes.byteLength);
  bytesCopy.set(bestBytes);
  return new File([bytesCopy.buffer], outName, {
    type: "application/octet-stream",
    lastModified: file.lastModified || Date.now(),
  });
}

/**
 * Удобный хелпер: гарантирует, что мы работаем с распакованным .ifc файлом,
 * и возвращает имя для логов/UI отдельно (исходное имя файла пользователя).
 */
export async function ensureIfcFile(file: File): Promise<{ ifcFile: File; originalName: string }> {
  const original = file.name;
  const ifcFile = await maybeUnzipIfczip(file);
  return { ifcFile, originalName: original };
}

// Только для тестов / отладки.
export const __testing__ = { isProbablyZip, looksLikeIfczipName };
